import { format } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import i18next from "i18next";
import React, { useEffect, useReducer, useState } from "react";
import { useCurrentUserLogin } from "../../hooks/useCurrentUserLogin";
import { toast } from "react-toastify";
import { useAdmin } from "../../context/adminContext";
import { useDesignSystem } from "../../context/designSystemContext";
import { showErrorToast } from "../../utils/errorToast";


import {
  useCreateUpdate,
  useEditUpdate,
  useGetCurrentUserTags,
  useGetInsights,
  useRiskTheme,
} from "../../api/hooks/useRiskThemes";
import { canEditRisk as getCanEditRisk } from "../../utils/canEditRisk";
import { pSBC } from "../../utils/pSBC";

import Collapsible from "../Collapsible";
import InsightsTag from "./InsightsTag";

export interface Update {
  id?: number;
  riskId: number;
  highlights: string;
  brutalFacts: string;
  nextSteps: string;
  yearMonth: string;
}

export interface UpdateState {
  editMode: boolean;
  highlights: string;
  brutal_facts: string;
  next_steps: string;
  original: {
    highlights: string;
    brutal_facts: string;
    next_steps: string;
  };
}

export type UpdatesAction =
  | { type: "SET_EDIT_MODE"; payload: boolean }
  | {
      type: "UPDATE_FIELD";
      field: "highlights" | "brutal_facts" | "next_steps";
      value: string;
    }
  | { type: "CANCEL_CHANGES" }
  | { type: "SAVE_CHANGES" };

const initialState: UpdateState = {
  editMode: false,
  highlights: "",
  brutal_facts: "",
  next_steps: "",
  original: {
    highlights: "",
    brutal_facts: "",
    next_steps: "",
  },
};

interface InsightProps {
  riskId: number;
}

function insightsReducer(
  state: UpdateState,
  action: UpdatesAction
): UpdateState {
  switch (action.type) {
    case "SET_EDIT_MODE":
      return { ...state, editMode: action.payload };
    case "UPDATE_FIELD":
      return { ...state, [action.field]: action.value };
    case "CANCEL_CHANGES":
      return { ...state, ...state.original, editMode: false };
    case "SAVE_CHANGES":
      return {
        ...state,
        original: {
          highlights: state.highlights,
          brutal_facts: state.brutal_facts,
          next_steps: state.next_steps,
        },
        editMode: false,
      };
    default:
      return state;
  }
}

const Insights: React.FC<InsightProps> = ({ riskId }) => {
  const { t } = i18next;
  const isAdmin = useAdmin();
  const { designSystem } = useDesignSystem();
  const currentUserLogin = useCurrentUserLogin();

  const { data: currentUserTags } = useGetCurrentUserTags();

  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(false);
  const [filter, setFilter] = useState<{
    month: string | undefined;
    year: number;
  }>({
    month: String(new Date().getMonth() + 1).padStart(2, "0"),
    year: new Date().getFullYear(),
  });
  const [insightsState, dispatch] = useReducer(insightsReducer, {
    ...initialState,
  });
  const {
    data: riskTheme,
  } = useRiskTheme(`${riskId}`);

  const { data } = useGetInsights(riskId, filter.year, filter.month);
  const { mutate: createUpdate, isPending: isPendingCreateUpdate } =
    useCreateUpdate(riskId);
  const { mutate: editUpdate, isPending: isPendingEditUpdate } =
    useEditUpdate(riskId);

  const currentDate = new Date();
  const month = filter.month
    ? format(
        new Date(currentDate.getFullYear(), parseInt(filter.month) - 1, 1),
        "MMMM",
        {
          locale: i18next.language === "ptBr" ? ptBR : enUS,
        }
      )
    : format(currentDate, "MMMM", {
        locale: i18next.language === "ptBr" ? ptBR : enUS,
      });
  const year = filter.year || currentDate.getFullYear();

  const isCurrentYear = filter.year === new Date().getFullYear();
  const isCurrentMonth =
    filter.month === String(new Date().getMonth() + 1).padStart(2, "0");
  const isEditable = isCurrentYear && (isCurrentMonth || !filter.month);

  const canEditRisk = getCanEditRisk({
    isAdmin: Boolean(isAdmin),
    riskTheme,
    currentUserLogin,
    currentUserTags,
  });

  const canEdit = isEditable && canEditRisk;

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter((prev) => ({ ...prev, month: e.target.value }));
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter((prev) => ({ ...prev, year: parseInt(e.target.value) }));
  };

  const months = Array.from({ length: 12 }, (_, i) => {
    const monthNumber = String(i + 1).padStart(2, "0");
    const date = new Date(currentDate.getFullYear(), i, 1);
    return {
      value: monthNumber,
      label: format(date, "MMMM", {
        locale: i18next.language === "ptBr" ? ptBR : enUS,
      }),
    };
  });

  const years = Array.from(
    { length: 5 },
    (_, i) => currentDate.getFullYear() - 2 + i
  );

  const handleInputChange = (
    field: "highlights" | "brutal_facts" | "next_steps",
    value: string
  ) => {
    dispatch({ type: "UPDATE_FIELD", field, value });
  };

  const handleCancel = () => {
    dispatch({ type: "CANCEL_CHANGES" });
  };

  const handleSave = () => {
    if (!navigator.onLine) {
      return toast.error(t("connectionError"));
    }

    const newUpdate: Update = {
      id: data?.id || -1,
      yearMonth: format(new Date(), "yyyy-MM"),
      highlights: insightsState.highlights,
      brutalFacts: insightsState.brutal_facts,
      nextSteps: insightsState.next_steps,
      riskId,
    };

    if (newUpdate.id === -1) {
      delete newUpdate.id;
      createUpdate(newUpdate, {
        onSuccess: () => {
          dispatch({ type: "SAVE_CHANGES" });
        },
        onError: (err: any) => showErrorToast(err, t("connectionError")),
      });
    }

    if (newUpdate.id !== -1 && newUpdate.id !== undefined) {
      editUpdate(newUpdate, {
        onSuccess: () => {
          dispatch({ type: "SAVE_CHANGES" });
        },
        onError: (err: any) => showErrorToast(err, t("connectionError")),
      });
    }
  };

  useEffect(() => {
    dispatch({
      type: "UPDATE_FIELD",
      field: "highlights",
      value: data?.highlights || "",
    });
    dispatch({
      type: "UPDATE_FIELD",
      field: "brutal_facts",
      value: data?.brutalFacts || "",
    });
    dispatch({
      type: "UPDATE_FIELD",
      field: "next_steps",
      value: data?.nextSteps || "",
    });
    dispatch({ type: "SAVE_CHANGES" });
  }, [data]);

  useEffect(() => {
    if (filter.month) {
      return dispatch({
        type: "SET_EDIT_MODE",
        payload: false,
      });
    }

    if (!filter.month && insightsState.editMode) {
      return dispatch({
        type: "SET_EDIT_MODE",
        payload: true,
      });
    }
  }, [filter.month]);

  return (
    <div className="p-4">
      <Collapsible
        header={
          <div className="flex items-center gap-4">
            <p className="font-openSans font-bold text-[16px] leading-[16px] tracking-[0.18px] min-w-[7rem]" style={{ color: `${pSBC(0.9, designSystem?.cssBasics.primaryColor)}` }}>
              {t("insight_filter_updates", { month, year })}
            </p>
            <div className="flex gap-2">
              <select
                value={filter.year}
                onChange={handleYearChange}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white hover:border-quaternary focus:border-quaternary focus:ring-1 focus:ring-quaternary outline-none transition-colors duration-200 cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%236B7280%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.5em_1.5em] bg-[right_0.5rem_center] bg-no-repeat pr-10"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <select
                value={filter.month || ""}
                onChange={handleMonthChange}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white hover:border-quaternary focus:border-quaternary focus:ring-1 focus:ring-quaternary outline-none transition-colors duration-200 cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%236B7280%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.5em_1.5em] bg-[right_0.5rem_center] bg-no-repeat pr-10"
              >
                <option value="">{t("select_month")}</option>
                {months.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        }
        editable={canEdit}
        isOpen={isCollapsibleOpen}
        onClose={() => setIsCollapsibleOpen(false)}
        onOpen={() => setIsCollapsibleOpen(true)}
        onEdit={() => {
          setIsCollapsibleOpen(true);
          dispatch({ type: "SET_EDIT_MODE", payload: true });
        }}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-row justify-between gap-6">
            <div className="flex flex-col w-1/3 gap-2 max-h-[260px]">
              <div className="px-2">
                <InsightsTag theme="highlights" />
              </div>
              <textarea
                value={insightsState.highlights}
                disabled={!insightsState.editMode}
                onChange={(e) =>
                  handleInputChange("highlights", e.target.value)
                }
                className={`w-full p-3 rounded-md resize-none min-h-[100px] max-h-[300px] text-sm font-openSans text-gray-700 bg-white scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 ${
                  insightsState.editMode
                    ? "border border-gray-300 focus:border-quaternary focus:ring-0 focus:outline-none"
                    : "border-none"
                }`}
                style={{ height: "auto" }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${Math.min(
                    target.scrollHeight,
                    300
                  )}px`;
                }}
              />
            </div>

            <div className="flex flex-col w-1/3 gap-2">
              <div className="px-2">
                <InsightsTag theme="facts" />
              </div>
              <textarea
                value={insightsState.brutal_facts}
                disabled={!insightsState.editMode}
                onChange={(e) =>
                  handleInputChange("brutal_facts", e.target.value)
                }
                className={`w-full p-3 rounded-md resize-none min-h-[100px] max-h-[300px] text-sm font-openSans text-gray-700 bg-white scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 ${
                  insightsState.editMode
                    ? "border border-gray-300 focus:border-quaternary focus:ring-0 focus:outline-none"
                    : "border-none"
                }`}
                style={{ height: "auto" }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${Math.min(
                    target.scrollHeight,
                    300
                  )}px`;
                }}
              />
            </div>

            <div className="flex flex-col w-1/3 gap-2">
              <div className="px-2">
                <InsightsTag theme="next" />
              </div>
              <textarea
                value={insightsState.next_steps}
                disabled={!insightsState.editMode}
                onChange={(e) =>
                  handleInputChange("next_steps", e.target.value)
                }
                className={`w-full p-3 rounded-md resize-none min-h-[100px] max-h-[300px] text-sm font-openSans text-gray-700 bg-white scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 ${
                  insightsState.editMode
                    ? "border border-gray-300 focus:border-quaternary focus:ring-0 focus:outline-none"
                    : "border-none"
                }`}
                style={{ height: "auto" }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${Math.min(
                    target.scrollHeight,
                    300
                  )}px`;
                }}
              />
            </div>
          </div>

          {insightsState.editMode && isEditable && (
            <div className="flex flex-row w-full items-center justify-end">
              <div className="flex flex-row w-full max-w-64 gap-4 items-center">
                <button
                  className="font-openSans font-bold transition duration-300 w-full ease-in-out px-2 py-2 text-xs font-primary font-semibold rounded-md flex items-center justify-center border-[1px] hover:bg-gray-100 disabled:bg-slate-200"
                  style={{ color: `${pSBC(0.9, designSystem?.cssBasics.secondaryColor)}`, borderColor: `${pSBC(0.9, designSystem?.cssBasics.secondaryColor)}` }}
                  onClick={handleCancel}
                >
                  {t("cancel")}
                </button>
                <button
                  className="font-openSans font-bold transition duration-300 w-full ease-in-out px-2 py-2 text-xs font-primary font-semibold rounded-md flex items-center justify-center text-white hover:bg-teal-800 disabled:bg-slate-200 focus:outline-none"
                  style={{
                    backgroundColor: designSystem?.cssBasics.primaryColor,
                  }}
                  onClick={handleSave}
                >
                  {t("save")}
                </button>
              </div>
            </div>
          )}
        </div>
      </Collapsible>
    </div>
  );
};

export default Insights;
